/**
 * VH-113: QR Per-Exercise Set Capture — public capture API client.
 *
 * These endpoints are PUBLIC (token-authed, no user login). The capture page
 * must work for an unauthenticated visitor who arrives by scanning a printed
 * QR code, so this client deliberately does NOT reuse the shared `api`
 * instance from `api.js`:
 *
 *   - It sends NO `Authorization`/JWT header (the URL token is the capability).
 *   - It has NO `401 -> /login` response interceptor, so a public visitor is
 *     never bounced to the login screen.
 */
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || ''

const captureClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Load the exercise + its planned sets for a capture token.
 * Invalid/unknown token resolves to a 404 (handled by the caller).
 */
export const getCapture = (token) => captureClient.get(`/api/v1/capture/${token}`)

/**
 * Persist the captured actuals.
 * @param {string} token
 * @param {{ sets: Array<{
 *   set_number: number, set_type: string,
 *   actual_reps: number|null, actual_weight: number|null,
 *   weight_notation: string|null
 * }> }} payload
 */
export const saveCapture = (token, payload) =>
  captureClient.post(`/api/v1/capture/${token}`, payload)

/**
 * VH-114: Load the WHOLE session for a session-scoped capture token.
 * Returns header (module name, week, date, `locked`) + a list of exercise
 * blocks, each carrying its type (`per_set`) and either pre-filled `sets` or
 * an embedded metric-field template + targets + saved `metric_values`.
 * Invalid/unknown token resolves to a 404 (handled by the caller).
 */
export const getSessionCapture = (token) =>
  captureClient.get(`/api/v1/capture/session/${token}`)

/**
 * VH-114: Persist actuals for the whole session in one request.
 * VH-120: `complete` (null = Save only, true/false = Mark Complete/Incomplete).
 * @param {string} token
 * @param {{ exercises: Array<{
 *   session_exercise_id: number,
 *   sets?: Array<{ set_number: number, set_type: string,
 *     actual_reps: number|null, actual_weight: number|null,
 *     weight_notation: string|null }>,
 *   metric_values?: Record<string, number|string>
 * }>, session_date?: string|null, complete?: boolean|null }} payload
 */
export const saveSessionCapture = (token, payload) =>
  captureClient.post(`/api/v1/capture/session/${token}`, payload)

export default { getCapture, saveCapture, getSessionCapture, saveSessionCapture }
